import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UserActivityCategory, UserActivityTypes } from '@pascal/iframe-service';
import { observer } from 'mobx-react-lite';

import { dotFormatter, formatFreeDropWidgetTime, getFreeDropReachedLevel, getFreeDropTierGoals, getFreeDropWidgetPosition, roundNumber, saveFreeDropWidgetPosition } from '../../../../../helpers';
import { useStore, useTranslation, useVerticalDrag } from '../../../../../hooks';
import { NotificationItem } from '../../../../../store/notification-store';
import { FREE_DROP_WIDGET_INTRO_DELAY, T } from '../../../../../constants';
import { FreeDropNotificationType } from '../../../../../enums';
import { FreeDropDrop } from '../../../../../models';
import { PromoToolProgress } from './promo-tool-progress';

import './style.scss';

// resuming the queue in the same frame the widget paints shows the row already opened instead of sliding it in
const NOTIFICATION_RESUME_DELAY = 1_000;

// length of the promo-tool-highlight glow in style.scss
const HIGHLIGHT_DURATION = 800;

interface FreeDropPromoToolProps {
  nextDrop: FreeDropDrop | null;
  nextDropDate: Date | null;
}

export const FreeDropPromoTool: React.FC<FreeDropPromoToolProps> = observer(({ nextDrop, nextDropDate }) => {

  const t = useTranslation();
  const { freeDropStore, gameStore, modalStore, cashbackStore, tournamentStore, userActivityStore, freeDropNotificationStore } = useStore();
  const { collectedAmount, showProgressBar, showDetailsPopup, freeDropInfo, themeConfig } = freeDropStore;
  const { currencyCode, precision, rate } = gameStore;
  const { activeNotification } = freeDropNotificationStore;
  const { anyFeaturePopupOpen } = modalStore;

  const previousAmountRef = useRef(collectedAmount);

  const [remainingTime, setRemainingTime] = useState(0);
  const [highlighting, setHighlighting] = useState(false);
  const [userExpanded, setUserExpanded] = useState(false);
  const [position, setPosition] = useState<number | null>(getFreeDropWidgetPosition);
  const [lastNotification, setLastNotification] = useState<NotificationItem<FreeDropNotificationType> | null>(null);

  const notificationsBlocked = !showProgressBar || showDetailsPopup || anyFeaturePopupOpen;
  // the default spot is taken by the rewards widget / tournament opener
  const stacked = cashbackStore.showCashback || tournamentStore.showTournamentOpener;

  const goals = useMemo(() => {
    if (!nextDrop || !freeDropInfo) return [];
    return getFreeDropTierGoals(nextDrop, currencyCode, freeDropInfo.currency, rate);
  }, [nextDrop, currencyCode, rate, freeDropInfo]);

  const currentLevel = getFreeDropReachedLevel(collectedAmount, goals);
  const isMaxLevel = !!goals.length && currentLevel === goals.length;

  // progress inside the current level, towards the next one
  const levelProgress = useMemo(() => {
    if (!goals.length) return 0;
    if (isMaxLevel) return 100;
    const levelStart = goals[currentLevel - 1] || 0;
    const levelRange = goals[currentLevel] - levelStart;
    if (levelRange <= 0) return 0;
    return Math.min(Math.max((collectedAmount - levelStart) / levelRange * 100, 0), 100);
  }, [goals, currentLevel, isMaxLevel, collectedAmount]);

  const progressText = useMemo(() => {
    // next level goal, the last one once all levels are reached
    const target = goals[Math.min(currentLevel, goals.length - 1)] || 0;
    return `${dotFormatter(roundNumber(collectedAmount, precision))} / ${dotFormatter(roundNumber(target, precision))} ${currencyCode}`;
  }, [goals, currentLevel, collectedAmount, precision, currencyCode]);

  // short glow whenever the player's progress grows (not when it resets after a drop)
  useEffect(() => {
    const grew = collectedAmount > previousAmountRef.current;
    previousAmountRef.current = collectedAmount;
    if (!grew) return;
    setHighlighting(true);
    const timeout = setTimeout(() => setHighlighting(false), HIGHLIGHT_DURATION);
    return () => {
      clearTimeout(timeout);
      setHighlighting(false);
    };
  }, [collectedAmount]);

  useEffect(() => {
    if (!nextDrop || !nextDropDate) return;
    const tick = () => {
      const remaining = Math.max(nextDropDate.getTime() - Date.now(), 0);
      setRemainingTime(remaining);
      freeDropStore.handleCountdownTick(nextDrop.dropDate, remaining);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [nextDrop, nextDropDate, freeDropStore]);

  useEffect(() => {
    const timeout = setTimeout(() => freeDropStore.showWidgetIntro(), FREE_DROP_WIDGET_INTRO_DELAY);
    return () => clearTimeout(timeout);
  }, [freeDropStore]);

  // hold notifications back while the widget is hidden or covered by a popup, resume once it is visible again
  useEffect(() => {
    if (notificationsBlocked) {
      freeDropNotificationStore.setPaused(true);
      return;
    }
    const timeout = setTimeout(() => freeDropNotificationStore.setPaused(false), NOTIFICATION_RESUME_DELAY);
    return () => clearTimeout(timeout);
  }, [notificationsBlocked, freeDropNotificationStore]);

  useEffect(() => {
    return () => {
      freeDropNotificationStore.clearQueue();
      freeDropNotificationStore.setPaused(true);
    };
  }, [freeDropNotificationStore]);

  useEffect(() => {
    if (!showProgressBar) setUserExpanded(false);
  }, [showProgressBar]);

  useEffect(() => {
    if (activeNotification) setLastNotification(activeNotification);
  }, [activeNotification]);

  const handleDragEnd = useCallback((value: number) => {
    setPosition(value);
    saveFreeDropWidgetPosition(value);
  }, []);

  const { ref: wrapperRef, onPointerDown } = useVerticalDrag<HTMLDivElement>({ onDragEnd: handleDragEnd });

  const expand = useCallback(() => {
    setUserExpanded(true);
  }, []);

  const collapse = useCallback(() => {
    setUserExpanded(false);
    freeDropNotificationStore.hideActiveNotification();
  }, [freeDropNotificationStore]);

  const openDetailsPopup = useCallback(() => {
    userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.FreeDropsWidget
    });
    freeDropStore.setShowDetailsPopup(true);
  }, [userActivityStore, freeDropStore]);

  if (!nextDrop) return null;

  const expanded = userExpanded || !!activeNotification;
  // while the row slides back keep the notification it was opened for instead of flashing the progress text
  const notificationMessage = activeNotification ? activeNotification.message : (!userExpanded && lastNotification?.message) || '';
  const wrapperStyle = position === null ? undefined : { '--free-drop-widget-position': position } as CSSProperties;

  return (
    <div
      ref={wrapperRef}
      style={wrapperStyle}
      onPointerDown={onPointerDown}
      className={`promo-tool-wrapper ${showProgressBar ? 'show' : ''} ${stacked ? 'stacked' : ''} ${position === null ? '' : 'positioned'}`}
    >
      <div className="promo-tool-holder">
        <div className={`romo-tool-holder-inner ${expanded ? 'hidden' : ''} ${highlighting ? 'highlight' : ''}`} onClick={expand}>
          <div className="promo-tool-inner">
            <PromoToolProgress progress={levelProgress} level={currentLevel} />
            <img className="svg-img" src={themeConfig.widgetIcon} alt="" draggable={false} />
          </div>
          <p className="promo-tool-time">
            {formatFreeDropWidgetTime(remainingTime, true)}
          </p>
        </div>
        <div className="promo-tool-move-row-viewport">
          <div className={`promo-tool-move-row ${expanded ? 'open' : ''}`}>
            <div className="promo-tool-move-col">
              <img className="moved-row-img" src={themeConfig.widgetIcon} alt="" draggable={false} />
            </div>
            <div className="promo-tool-move-col-middle">
              <div className="promo-tool-move-col-middle-body">
                <div className="promo-tool-move-col-header">
                  <p className="promo-tool-move-col-title">
                    {t(T.FREE_DROP_NEXT_FREE_SPIN_DROP_IN)}
                  </p>
                  <span className="promo-tool-move-col-num">
                    {formatFreeDropWidgetTime(remainingTime)}
                  </span>
                </div>
                <div className={`promo-tool-progress-holder ${isMaxLevel ? 'active' : ''} ${highlighting ? 'highlight' : ''}`}>
                  {
                    goals.map((goal, index) => (
                      <span key={index} className={`promo-tool-level ${index < currentLevel ? 'active' : ''}`}>
                        {t(T.LVL)} {index + 1}
                      </span>
                    ))
                  }
                </div>
                <p className="promo-tool-move-col-sub-text">
                  {notificationMessage || progressText}
                </p>
              </div>
              <div className="promo-tool-move-right-col">
                <span className="promo-tool-move-col-inner" onClick={openDetailsPopup}>
                  <i className="icon-info promo-tool-move-right-col-info" />
                </span>
              </div>
            </div>
            <div className="promo-tool-move-col" onClick={collapse}>
              <i className="promo-tool-icon icon-arrow-left" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
